import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Calendar, AlertTriangle, ArrowUpCircle, MinusCircle, ArrowDownCircle, Trash2, Edit3, PlayCircle, HandHeart, MoreVertical, Plus } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import AppLogo from '../components/AppLogo';
import CreateTaskDialog from '../components/tasks/CreateTaskDialog';
import EditTaskDialog from '../components/tasks/EditTaskDialog';

const PRIORITY_CONFIG = {
  high: { label: 'High', icon: ArrowUpCircle, color: 'text-rose-600', bg: 'bg-rose-50 text-rose-700 border-rose-200' },
  medium: { label: 'Medium', icon: MinusCircle, color: 'text-amber-600', bg: 'bg-amber-50 text-amber-700 border-amber-200' },
  low: { label: 'Low', icon: ArrowDownCircle, color: 'text-sky-600', bg: 'bg-sky-50 text-sky-700 border-sky-200' },
};

const STATUS_CONFIG = {
  pending: { label: 'To Do', color: 'bg-slate-100 text-slate-600' },
  in_progress: { label: 'In Progress', color: 'bg-cyan-50 text-cyan-700' },
  completed: { label: 'Done', color: 'bg-emerald-50 text-emerald-700' },
};

const TasksPage = () => {
  const { user } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [circles, setCircles] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedCircleId, setSelectedCircleId] = useState('');
  const [newTask, setNewTask] = useState({ title: '', description: '', assigned_to: '', due_date: '', priority: 'medium', needs_squad: false });

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, circlesRes] = await Promise.all([
        axios.get(`${API}/my-tasks`),
        axios.get(`${API}/circles`)
      ]);
      setTasks(tasksRes.data);
      setCircles(circlesRes.data);
      if (circlesRes.data.length > 0) {
        setSelectedCircleId(prev => {
          if (!prev) {
            axios.get(`${API}/circles/${circlesRes.data[0].id}/members`).then(r => setMembers(r.data)).catch(() => {});
            return circlesRes.data[0].id;
          }
          return prev;
        });
      }
    } catch (error) { toast.error('Could not load tasks'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchMembersForCircle = async (circleId) => {
    try {
      const res = await axios.get(`${API}/circles/${circleId}/members`);
      setMembers(res.data);
    } catch (e) { toast.error('Could not load members'); }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!selectedCircleId) { toast.error('Pick a circle first'); return; }
    try {
      await axios.post(`${API}/tasks`, { ...newTask, circle_id: selectedCircleId });
      toast.success(newTask.needs_squad ? 'Task posted! Your squad has been notified.' : 'Task added to your to-do list.');
      setNewTask({ title: '', description: '', assigned_to: '', due_date: '', priority: 'medium', needs_squad: false });
      setAddDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not create request'); }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    try {
      await axios.put(`${API}/tasks/${editingTask.id}`, {
        title: editingTask.title,
        description: editingTask.description,
        priority: editingTask.priority,
        due_date: editingTask.due_date,
        assigned_to: editingTask.assigned_to || null,
      });
      toast.success('Updated successfully!');
      setEditDialogOpen(false);
      setEditingTask(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not update'); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
      const statusMessages = { completed: 'Marked as done - great work!', in_progress: 'On it! Moved to In Progress.', pending: 'Moved back to To Do.' };
      toast.success(statusMessages[newStatus]);
    } catch (error) { toast.error('Could not update status'); }
  };

  const handleClaimTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/claim`);
      toast.success("Thank you for stepping up! You're making a difference.");
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not claim'); }
  };

  const handleUnclaimTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/unclaim`);
      toast.success('Task unclaimed — the squad can pick it up now.');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not unclaim'); }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    try {
      await axios.delete(`${API}/tasks/${taskToDelete}`);
      toast.success('Task removed');
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not delete'); }
  };

  const openEditDialog = (task) => {
    setEditingTask({ ...task });
    if (task.circle_id && task.circle_id !== selectedCircleId) {
      fetchMembersForCircle(task.circle_id);
    }
    setEditDialogOpen(true);
  };

  const filteredTasks = tasks.filter(t => {
    if (filter === 'mine') return t.assigned_to === user.id;
    if (filter === 'pending') return t.status === 'pending';
    if (filter === 'in_progress') return t.status === 'in_progress';
    if (filter === 'completed') return t.status === 'completed';
    if (filter === 'open') return !t.assigned_to && t.status !== 'completed';
    return true;
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    const statusOrder = { pending: 0, in_progress: 1, completed: 2 };
    if (statusOrder[a.status] !== statusOrder[b.status]) return statusOrder[a.status] - statusOrder[b.status];
    return (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
  });

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-200 border-t-cyan-600"></div></div>;
  }

  const getNextStatus = (current) => {
    if (current === 'pending') return 'in_progress';
    if (current === 'in_progress') return 'completed';
    return 'pending';
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
    if (status === 'in_progress') return <PlayCircle className="w-5 h-5 text-cyan-500" />;
    return <Circle className="w-5 h-5 text-slate-300 hover:text-cyan-400 transition-colors" />;
  };

  return (
    <div className="min-h-screen pb-24" data-testid="tasks-page">
      <header className="surface-header sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AppLogo />
            <div>
            <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Tasks</h1>
            <p className="text-xs text-muted-foreground">{sortedTasks.length} task{sortedTasks.length !== 1 ? 's' : ''} {filter !== 'all' ? `(${filter.replace('_', ' ')})` : ''}</p>
            </div>
          </div>
          <CreateTaskDialog
            open={addDialogOpen}
            onOpenChange={setAddDialogOpen}
            task={newTask}
            setTask={setNewTask}
            circles={circles}
            members={members}
            selectedCircleId={selectedCircleId}
            onCircleChange={(val) => { setSelectedCircleId(val); fetchMembersForCircle(val); }}
            onSubmit={handleCreateTask}
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-4">
        {/* Filters */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { value: 'all', label: 'All' },
            { value: 'mine', label: 'My Tasks' },
            { value: 'pending', label: 'To Do' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'open', label: 'Needs Help' },
            { value: 'completed', label: 'Done' }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              data-testid={`filter-${f.value}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
                filter === f.value
                  ? 'bg-cyan-600 text-white shadow-sm'
                  : 'bg-card text-muted-foreground border border-border/50 hover:bg-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Task List */}
        <div className="space-y-2">
          {sortedTasks.length > 0 ? (
            sortedTasks.map((task) => {
              const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
              const status = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
              const PriorityIcon = priority.icon;
              const isDue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';
              return (
                <div key={task.id} className={`surface-card p-4 transition-all duration-200 ${task.status === 'completed' ? 'opacity-60' : ''}`} data-testid={`task-${task.id}`}>
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => handleStatusChange(task.id, getNextStatus(task.status))}
                      className="mt-0.5 flex-shrink-0"
                      data-testid={`toggle-task-${task.id}`}
                      title={`Click to move to ${STATUS_CONFIG[getNextStatus(task.status)]?.label}`}
                    >
                      {getStatusIcon(task.status)}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                          {task.title}
                        </h4>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded-lg hover:bg-muted transition-colors flex-shrink-0" data-testid={`task-menu-${task.id}`}>
                              <MoreVertical className="w-4 h-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(task)} data-testid={`edit-task-${task.id}`}>
                              <Edit3 className="w-3.5 h-3.5 mr-2" /> Edit
                            </DropdownMenuItem>
                            {task.status !== 'in_progress' && task.status !== 'completed' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'in_progress')}>
                                <PlayCircle className="w-3.5 h-3.5 mr-2" /> Start
                              </DropdownMenuItem>
                            )}
                            {task.status !== 'completed' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'completed')}>
                                <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> Complete
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className="text-rose-600" onClick={() => { setTaskToDelete(task.id); setDeleteConfirmOpen(true); }} data-testid={`delete-task-${task.id}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-2" /> Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {task.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{task.description}</p>}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${status.color}`}>{status.label}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${priority.bg}`}>
                          <PriorityIcon className="w-3 h-3 inline mr-0.5 -mt-px" />{priority.label}
                        </span>
                        {task.circle_name && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{task.circle_name}</span>}
                        {task.due_date && (
                          <span className={`text-[10px] flex items-center gap-0.5 ${isDue ? 'text-rose-500 font-semibold' : 'text-muted-foreground'}`}>
                            {isDue && <AlertTriangle className="w-3 h-3" />}
                            <Calendar className="w-3 h-3" /> {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                        {task.assignee && <span className="text-[10px] text-muted-foreground">{task.assignee.name}</span>}
                        {task.claimed_by_name && task.status === 'in_progress' && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 font-medium">
                            Claimed by {task.claimed_by_name}
                          </span>
                        )}
                        {task.needs_squad === false && !task.assigned_to && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 font-medium">Personal</span>
                        )}
                      </div>
                      {task.needs_squad && !task.assigned_to && task.status !== 'completed' && !task.claimed_by && (
                        <Button size="sm" variant="outline" className="mt-2.5 h-7 text-xs rounded-full border-cyan-200 text-cyan-600 hover:bg-cyan-50" onClick={() => handleClaimTask(task.id)} data-testid={`claim-task-${task.id}`}>
                          <HandHeart className="w-3 h-3 mr-1" /> I can help with this
                        </Button>
                      )}
                      {task.status !== 'completed' && (task.claimed_by === user?.id || task.assigned_to === user?.id) && (
                        <Button size="sm" variant="outline" className="mt-2.5 h-7 text-xs rounded-full border-slate-200 text-muted-foreground hover:bg-slate-50" onClick={() => handleUnclaimTask(task.id)} data-testid={`unclaim-task-${task.id}`}>
                          Unclaim
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-16 px-4 surface-card">
              <HandHeart className="w-12 h-12 text-cyan-200 mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground mb-1">
                {filter === 'all' ? 'No tasks yet' : `No ${filter.replace('_', ' ')} tasks`}
              </p>
              <p className="text-xs text-muted-foreground/60 mb-4">
                {filter === 'all' ? "Add a task to get started." : 'Try a different filter to see more.'}
              </p>
              {circles.length > 0 && filter === 'all' && (
                <Button size="sm" onClick={() => setAddDialogOpen(true)} className="btn-primary" data-testid="empty-add-task-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Task
                </Button>
              )}
            </div>
          )}
        </div>
      </main>

      <EditTaskDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        task={editingTask}
        setTask={setEditingTask}
        onSubmit={handleUpdateTask}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this request?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. The help request will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTask} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-task">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TasksPage;
